import React from 'react';
import { ProductContext, ProductContextProps } from '../context/ProductContext';
import { Product } from '../types/product';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';

export const productState = {
  items: new Map<string, Map<string, Product>>(),
  loading: true,
};

export const productSlice = createSlice({
  name: 'product',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      productState.items = new Map();
      productState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<Product[]>) => {
      productState.items = new Map();
      for (const item of action.payload) {
        const product = new Product(item);
        const existing = productState.items.get(product.productMasterDataId) || new Map();
        productState.items.set(product.productMasterDataId, existing.set(product.id, product));
      }
      state.key = v4();
    },
    add: (state, action: PayloadAction<Product[]>) => {
      for (const item of action.payload) {
        const product = new Product(item);
        const existing = productState.items.get(product.productMasterDataId) || new Map();
        productState.items.set(product.productMasterDataId, existing.set(product.id, product));
      }
      productState.items = new Map(productState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<Product[]>) => {
      for (const item of action.payload) {
        const product = new Product(item);
        const existing = productState.items.get(product.productMasterDataId);
        if (existing) {
          existing.delete(product.id);
          if (!existing.size) {
            productState.items.delete(product.productMasterDataId);
          }
        }
      }
      productState.items = new Map(productState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      productState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface ProductProviderProps {
  children: React.ReactNode;
}

export default function ProductProvider({ children }: ProductProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.products.key);

  const productContext: ProductContextProps = {
    products: productState.items,
    productsLoading: productState.loading,
    setProducts: (products: Map<string, Map<string, Product>>) =>
      dispatch(productSlice.actions.set([...products.values()].flatMap(m => [...m.values()]))),
  };

  return <ProductContext.Provider value={productContext}>{children}</ProductContext.Provider>;
}
